<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
    overlay-opacity="0.85"
    content-class="modern-time-tracker-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-timer-outline</v-icon>
          Time Tracker
        </div> 
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Main content -->
      <v-card-text class="px-6 py-4">
        <v-row>
          <v-col cols="12" class="py-2">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-2" v-if="shift">
              {{shift.title}}
            </div>
            <div class="text-body-1 grey--text text--darken-2">
              {{ startDateStr }}
            </div>
          </v-col>
          <!-- Only show shift info, not dropdown, if selectedShiftObj is set -->
          <v-col cols="12" class="py-2" v-if="selectedShiftObj">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-2">
              Shift 
            </div>
            <div class="text-body-1 grey--text text--darken-2">
              {{ selectedShiftObj.shift.title || "Shift" }}
            </div>
          </v-col>
          <!-- <v-col cols="12" class="py-2" v-else>
            <v-select
              v-model="selectedShiftId"
              :items="responses"
              label="Select Shift*"
              item-text="shift.title"
              item-value="_id"
              outlined
              dense
              prepend-inner-icon="mdi-clock-outline"
              :disabled="running"
              class="modern-input"
            />
          </v-col> -->

          <v-col cols="12" class="py-2 d-flex justify-center">
            <div class="time-display primary--text">
              {{ formatSeconds() }}
            </div>
          </v-col>

          <v-col cols="12" class="py-2">
            <v-textarea
              v-model="memo"
              label="Work Description"
              outlined
              dense
              counter="140"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="Brief description of work completed"
              :disabled="running"
              @input="onChange"
              class="modern-input"
              rows="2"
            />
          </v-col>
        </v-row>

        <v-expand-transition>
          <v-alert
            v-if="error"
            dense
            type="error"
            class="mt-4"
            transition="scale-transition"
          >
            {{ error }}
          </v-alert>
        </v-expand-transition>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="startTime"
          :loading="loading"
          :disabled="!(shift && shift._id) || !selectedShiftId"
          class="px-6 save-btn"
          large
          outlined
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>{{ running ? "mdi-stop" : "mdi-play" }}</v-icon>
          {{ running ? "Stop" : "Start" }}
        </v-btn>
      </v-card-actions>

      <!-- Snackbar for notifications -->
      <v-snackbar v-model="snackbar" :timeout="4000" color="primary" bottom>
        {{ snackMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import dateFormat from "dateformat";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    need_id: String,
    onCloseDialog: {
      type: Function
    },
    shift: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      startDate: null,
      loading: false,
      snackbar: false,
      snackMessage: null,
      error: null,
      running: false,
      startDateStr: "December 31, 2021",
      memo: "",
      timerSeconds: 0,
      timerInterval: null, // store interval id
      responses: [],
      selectedShiftId: null, // use this instead of mutating prop
      // Remove rules.required, not needed pnymore
      rules: {}
    };
  },
  methods: {
    ...mapActions("timeslot", {
      startTimeTracking: "startTimeTracking",
      stopTimeTracking: "stopTimeTracking", 
      getTimeTracking: "getTimeTracking",
      findTimer: "findTimer"
    }),
    onClose() {
      this.memo = "";
      this.running = false;
      this.onCloseDialog();
    },
    onChange() {
      this.error = null;
    },
    startCounting() {
      if (this.running) {
        this.running = false;
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      } else {
        this.running = true;
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
        }
        this.timerInterval = setInterval(() => {
          this.timerSeconds++;
        }, 1000);
      }
    },
    formatSeconds() {
      // if this.running is false, set to true
      if (!this.running) {
        if (this.timerInfo && this.timerInfo.res && this.timerInfo.res.status === "Tracking") {
          this.running = true;
        }
      }
      const hours = Math.floor(this.timerSeconds / 3600)
        .toString()
        .padStart(2, "0");
      const minutes = Math.floor((this.timerSeconds / 60) % 60)
        .toString()
        .padStart(2, "0");
      const seconds = Math.floor(this.timerSeconds % 60)
        .toString()
        .padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    async startTime() {
      this.error = null;
      if (!this.running) {
        this.loading = true;
        try {
          const res = await this.startTimeTracking({
            memo: this.memo,
            response_id: (() => {
              if (this.shift && Array.isArray(this.shift.responses)) {
                const found = this.shift.responses.find(
                  r => r.user_id === this.profile._id
                );
                return found ? found._id : this.selectedShiftId;
              }
              return this.selectedShiftId;
            })(),
            user_id: this.profile._id
          });
          // store this in the store value timerInfo in need.js
          // this.$store.commit("need/setTimerInfo", {
          //   ...res,
          //   shiftHours: this.shift?.hours
          // });
          this.$store.commit("need/setTimerInfo", {
            res: {
              ...res,
              status: "Tracking",
              shift_id: this.shift._id,
              response_id: res.response_id || this.selectedShiftId,
              start: new Date().toISOString(),
            },
            shiftHours: this.shift?.hours,
            ifTracking: true,
            length: 1
          });

          this.running = true;
          if (this.timerInterval) clearInterval(this.timerInterval);
          this.timerInterval = setInterval(() => {
            this.timerSeconds++;
          }, 1000);
        } catch (error) {
          this.snackMessage =
            error.response?.data?.message || "Failed to start tracking";
          this.snackbar = true;
        } finally {
          this.loading = false;
        }
      } else {
        this.loading = true;
        try {
          await this.stopTimeTracking({
            _id: this.timerInfo && this.timerInfo.res ? this.timerInfo.res._id : null
          });
          this.onClose();
          // update the timerInfo in the store with timerInfo.status = Completed
          this.$store.commit("need/setTimerInfo", {
            ...(this.timerInfo && this.timerInfo.res ? this.timerInfo.res : {}),
            status: "Completed"
          });
        } catch (error) {
          this.snackMessage =
            error.response?.data?.message || "Failed to stop tracking";
          this.snackbar = true;
        } finally {
          this.loading = false;
        }
      }
    },
    convertTZ(date, tzString) {
      if (!date) {
        return new Date(
          new Date().toLocaleString("en-US", {
            timeZone: tzString || "America/New_York"
          })
        );
      }
      return new Date(
        (typeof date === "string"
          ? new Date(date)
          : date
        ).toLocaleString("en-US", { timeZone: tzString || "America/New_York" })
      );
    },
    async findTimerInfo() {
      try {
        const timerInfo2 = await this.findTimer();
        if (timerInfo2) {
          this.$store.commit("need/setTimerInfo", {
            ...timerInfo2,
            shiftHours: this.shift?.hours
          });
          return timerInfo2;
        }
      } catch (e) {
        // handle error if needed
      }
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    }),
    ...mapState("need", {
      timerInfo: "timerInfo"
    }),
    selectedShiftObj() {
      // Use allShifts if you want to access the global shifts
      // Example: return this.allShifts.find(s => s._id === this.selectedShiftId) || null;
      // Or use responses as before if you want only the user's responses
      if (this.selectedShiftId && Array.isArray(this.responses)) {
        return (
          this.responses.find(r => r.shift._id === this.selectedShiftId) || null
        );
      }
      return null;
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        //this.getTimerInfo();
        this.today = this.convertTZ("", this.timezone);
        this.startDateStr = dateFormat(this.today, "dddd, mmmm d, yyyy");
      }
    },
    shift: {
      immediate: true,
      handler(newVal) {
        // If shift prop is passed, set selectedShiftId to its _id
        if (newVal && newVal._id) {
          this.selectedShiftId = newVal._id;
        }
      }
    },
    timerInfo(newVal) {
      if (newVal && newVal.status === "Completed") {
        this.running = false;
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
        this.timerSeconds = 0;
        this.memo = "";
        this.snackMessage = "Time tracking completed successfully!";
        this.snackbar = true;
      }
      if (newVal && newVal.status === "Tracking") {
        this.running = true;
        const startTime = this.convertTZ(newVal.start, this.timezone);
        const currentTime = this.convertTZ("", this.timezone);
        this.timerSeconds = Math.floor((currentTime - startTime) / 1000);
        this.memo = newVal.memo || "";
        this.selectedShiftId = newVal.shift_id;
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
          this.timerSeconds++;
        }, 1000);
      }
    }
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  },
  mounted() {
    // if not timerInfo, fetch it
    if (!this.timerInfo) {
      this.running = false;
      this.findTimerInfo();
    }
    this.today = this.convertTZ("", this.timezone);
    this.startDateStr = dateFormat(this.today, "dddd, mmmm d, yyyy");
    //this.getTimerInfo();
    // if timerInfo is already set in the store, use it
 if (this.timerInfo && this.timerInfo.res && this.timerInfo.res.status === "Tracking") {
      this.running = true;
      const startTime = this.convertTZ(this.timerInfo.res.start, this.timezone);
      const currentTime = this.convertTZ("", this.timezone);
      this.timerSeconds = Math.floor((currentTime - startTime) / 1000);
      this.memo = this.timerInfo.res.memo || "";
      this.selectedShiftId = this.timerInfo.res.response_id;
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        this.timerSeconds++;
      }, 1000);
    }
  }
};
</script>

<style scoped>
.modern-time-tracker-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.time-display {
  font-family: "Poppins-SemiBold";
  font-size: 50px;
  line-height: 1;
  margin: 16px 0;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-time-tracker-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-time-tracker-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-time-tracker-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-time-tracker-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}

@media (max-width: 600px) {
  .time-display {
    font-size: 40px;
  }

  .v-card__actions {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}
</style>
